import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Plane, ArrowRight, ChevronDown, ChevronUp, Clock, Filter,
  X, Check, ArrowLeft, Shield, Leaf, SlidersHorizontal
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { trackEvent } from "@/components/TrackingScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Progress Bar ───
function ProgressBar({ step }) {
  const { t } = useTranslation();
  const steps = [
    { num: 1, label: t("results.progress_search") },
    { num: 2, label: t("results.progress_info") },
    { num: 3, label: t("results.progress_checkout") },
  ];
  return (
    <div className="flex items-center justify-center gap-0 max-w-md mx-auto" data-testid="progress-bar">
      {steps.map((s, i) => (
        <div key={s.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              s.num <= step ? "bg-[#f97316] text-white" : "bg-[#e2e8f0] text-[#6b7280]"
            }`}>
              {s.num < step ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className={`text-xs mt-1 font-medium ${s.num <= step ? "text-[#1e3a5f]" : "text-[#6b7280]"}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-14px] ${
              s.num < step ? "bg-[#f97316]" : "bg-[#e2e8f0]"
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Flight Card ───
function FlightCard({ flight, onSelect }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const dayDiff = useMemo(() => {
    if (!flight.departure_date || !flight.arrival_date) return 0;
    const dep = new Date(flight.departure_date);
    const arr = new Date(flight.arrival_date);
    return Math.floor((arr - dep) / 86400000);
  }, [flight.departure_date, flight.arrival_date]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`flight-card-${flight.id}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Airline info */}
          <div className="flex items-center gap-3 sm:w-40 shrink-0">
            {flight.airline_logo ? (
              <img src={flight.airline_logo} alt={flight.airline} className="w-8 h-8 object-contain" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#f8fafc] flex items-center justify-center">
                <Plane className="w-4 h-4 text-[#1e3a5f]" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-[#1f2937]">{flight.airline}</p>
              <p className="text-xs text-[#6b7280]">{flight.flight_number}</p>
            </div>
          </div>

          {/* Flight times */}
          <div className="flex-1 flex items-center gap-3 sm:gap-4">
            <div className="text-right">
              <p className="text-lg font-bold text-[#1f2937] font-['Outfit']">{flight.departure_time}</p>
              <p className="text-xs text-[#6b7280] font-medium">{flight.departure_code}</p>
            </div>

            <div className="flex-1 flex flex-col items-center min-w-[100px]">
              <p className="text-xs text-[#6b7280] mb-1">{flight.duration_text}</p>
              <div className="w-full flex items-center">
                <div className="w-2 h-2 rounded-full border-2 border-[#1e3a5f]" />
                <div className="flex-1 h-px bg-[#d1d5db] relative">
                  {flight.stops > 0 && flight.layovers?.map((_, i) => (
                    <div key={i} className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#f97316]"
                      style={{ left: `${((i + 1) / (flight.stops + 1)) * 100}%` }} />
                  ))}
                </div>
                <Plane className="w-3 h-3 text-[#1e3a5f] -rotate-45" />
              </div>
              <p className="text-xs text-[#6b7280] mt-1">
                {flight.stops === 0 ? t("results.nonstop") : flight.stops_detail}
              </p>
            </div>

            <div className="text-left">
              <div className="flex items-baseline gap-1">
                <p className="text-lg font-bold text-[#1f2937] font-['Outfit']">{flight.arrival_time}</p>
                {dayDiff > 0 && (
                  <span className="text-xs text-[#f97316] font-medium">+{dayDiff}</span>
                )}
              </div>
              <p className="text-xs text-[#6b7280] font-medium">{flight.arrival_code}</p>
            </div>
          </div>

          {/* Price + Select */}
          <div className="flex items-center gap-4 sm:gap-6 sm:w-48 justify-end">
            <div className="text-right">
              {flight.original_price > 0 && (
                <p className="text-xs text-[#6b7280] line-through">${flight.original_price}</p>
              )}
              <p className="text-2xl font-bold text-[#f97316] font-['Outfit']">${flight.our_price}</p>
              <p className="text-[10px] text-[#6b7280]">USD</p>
            </div>
            <button
              onClick={() => onSelect(flight)}
              className="bg-[#f97316] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#ea580c] transition-all hover:-translate-y-0.5 hover:shadow-lg whitespace-nowrap flex items-center gap-1.5"
              data-testid={`select-flight-${flight.id}`}
            >
              {t("results.select")}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {flight.is_direct && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
              <Check className="w-3 h-3" /> {t("results.direct_flight")}
            </span>
          )}
          {flight.is_best_for_visa && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
              <Shield className="w-3 h-3" /> {t("results.best_for_visa")}
            </span>
          )}
          {flight.carbon_emissions && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#f8fafc] text-[#6b7280] flex items-center gap-1">
              <Leaf className="w-3 h-3" /> {flight.carbon_emissions}
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs text-[#1e3a5f] font-medium flex items-center gap-1 hover:text-[#f97316] transition-colors"
          data-testid={`flight-details-toggle-${flight.id}`}
        >
          {expanded ? t("results.hide_details") : t("results.flight_details")}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 sm:px-6 pb-5 border-t border-[#f1f5f9] pt-4 bg-[#fafbfc]">
              {flight.legs?.map((leg, i) => (
                <div key={i}>
                  {i > 0 && flight.layovers?.[i - 1] && (
                    <div className="flex items-center gap-2 my-3 pl-6">
                      <div className="w-px h-6 bg-[#e2e8f0]" />
                      <Clock className="w-3.5 h-3.5 text-[#f97316]" />
                      <span className="text-xs text-[#6b7280]">
                        {t("results.layover_in")} {flight.layovers[i - 1].airport} &middot; {flight.layovers[i - 1].duration}
                      </span>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    {leg.airline_logo ? (
                      <img src={leg.airline_logo} alt={leg.airline} className="w-6 h-6 object-contain mt-0.5" />
                    ) : (
                      <Plane className="w-4 h-4 text-[#6b7280] mt-1" />
                    )}
                    <div className="flex-1 text-xs">
                      <div className="flex items-center gap-2 text-[#1f2937] font-medium">
                        <span>{leg.departure_time} {leg.departure_code}</span>
                        <ArrowRight className="w-3 h-3 text-[#6b7280]" />
                        <span>{leg.arrival_time} {leg.arrival_code}</span>
                        <span className="text-[#6b7280]">&middot; {leg.duration_text}</span>
                      </div>
                      <div className="text-[#6b7280] mt-0.5 flex flex-wrap gap-x-3">
                        <span>{leg.airline} {leg.flight_number}</span>
                        {leg.aircraft && <span>{t("results.aircraft")}: {leg.aircraft}</span>}
                        <span>{leg.travel_class}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Filters Sidebar ───
function FiltersPanel({ filters, setFilters, totalResults, onClear }) {
  const { t } = useTranslation();
  return (
    <div data-testid="filters-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Outfit'] text-lg font-semibold text-[#1e3a5f]">{t("results.filter_title")}</h3>
        <button onClick={onClear} className="text-xs text-[#f97316] font-medium hover:underline" data-testid="clear-filters">
          {t("results.clear_filters")}
        </button>
      </div>

      <p className="text-sm text-[#6b7280] mb-6">
        {t("results.results_found", { count: totalResults })}
      </p>

      {/* Stops */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-[#1f2937] mb-3">{t("results.filter_stops")}</h4>
        {[
          { key: "nonstop", label: t("results.filter_nonstop") },
          { key: "1stop", label: t("results.filter_1stop") },
          { key: "2plus", label: t("results.filter_2plus") },
        ].map((opt) => (
          <label key={opt.key} className="flex items-center gap-2.5 mb-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.stops.includes(opt.key)}
              onChange={() => {
                setFilters((prev) => ({
                  ...prev,
                  stops: prev.stops.includes(opt.key)
                    ? prev.stops.filter((s) => s !== opt.key)
                    : [...prev.stops, opt.key],
                }));
              }}
              className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
              data-testid={`filter-${opt.key}`}
            />
            <span className="text-sm text-[#1f2937] group-hover:text-[#f97316] transition-colors">{opt.label}</span>
          </label>
        ))}
      </div>

      {/* Time of day */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-[#1f2937] mb-3">{t("results.filter_times")}</h4>
        {[
          { key: "morning", label: t("results.filter_morning") },
          { key: "afternoon", label: t("results.filter_afternoon") },
          { key: "evening", label: t("results.filter_evening") },
        ].map((opt) => (
          <label key={opt.key} className="flex items-center gap-2.5 mb-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={filters.times.includes(opt.key)}
              onChange={() => {
                setFilters((prev) => ({
                  ...prev,
                  times: prev.times.includes(opt.key)
                    ? prev.times.filter((t2) => t2 !== opt.key)
                    : [...prev.times, opt.key],
                }));
              }}
              className="w-4 h-4 rounded border-[#d1d5db] text-[#f97316] focus:ring-[#f97316]"
              data-testid={`filter-${opt.key}`}
            />
            <span className="text-sm text-[#1f2937] group-hover:text-[#f97316] transition-colors">{opt.label}</span>
          </label>
        ))}
      </div>

      {/* Sort */}
      <div>
        <h4 className="text-sm font-semibold text-[#1f2937] mb-3">{t("results.sort_by")}</h4>
        <select
          value={filters.sort}
          onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
          className="w-full border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm bg-white text-[#1f2937] focus:ring-2 focus:ring-[#1e3a5f] outline-none"
          data-testid="sort-select"
        >
          <option value="best">{t("results.sort_best")}</option>
          <option value="price">{t("results.sort_price")}</option>
          <option value="duration">{t("results.sort_duration")}</option>
          <option value="departure">{t("results.sort_departure")}</option>
        </select>
      </div>
    </div>
  );
}

// ─── Loading Skeletons ───
function FlightSkeleton() {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 sm:w-40">
          <Skeleton className="w-8 h-8 rounded-full" />
          <div>
            <Skeleton className="h-4 w-20 mb-1" />
            <Skeleton className="h-3 w-14" />
          </div>
        </div>
        <div className="flex-1 flex items-center gap-4">
          <Skeleton className="h-6 w-12" />
          <div className="flex-1">
            <Skeleton className="h-3 w-16 mx-auto mb-2" />
            <Skeleton className="h-0.5 w-full" />
            <Skeleton className="h-3 w-12 mx-auto mt-2" />
          </div>
          <Skeleton className="h-6 w-12" />
        </div>
        <div className="flex items-center gap-4 sm:w-48 justify-end">
          <div>
            <Skeleton className="h-3 w-10 mb-1" />
            <Skeleton className="h-8 w-14" />
          </div>
          <Skeleton className="h-10 w-24 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function SearchResults() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fromCode = searchParams.get("from") || "";
  const toCode = searchParams.get("to") || "";
  const date = searchParams.get("date") || "";
  const returnDate = searchParams.get("returnDate") || "";
  const travelers = searchParams.get("travelers") || "1";
  const tripType = searchParams.get("type") || "one-way";

  const [flights, setFlights] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnLoading, setReturnLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMock, setIsMock] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const isRoundTrip = tripType === "round-trip";

  const [filters, setFilters] = useState({
    stops: [],
    times: [],
    sort: "best",
  });

  // Fetch outbound flights
  useEffect(() => {
    const fetchFlights = async () => {
      if (!fromCode || !toCode || !date || fromCode === "Any" || toCode === "Any" || date === "Flexible") {
        setLoading(false);
        setError("incomplete");
        return;
      }

      setLoading(true);
      setError(null);
      setSelectedOutbound(null);
      setReturnFlights([]);

      try {
        const res = await axios.post(`${API}/flights/search`, {
          departure_id: fromCode,
          arrival_id: toCode,
          outbound_date: date,
          return_date: null,
          trip_type: "one_way",
          passengers: parseInt(travelers),
        });

        setFlights(res.data.flights || []);
        setSearchMeta(res.data.search_metadata || null);
        setIsMock(!!res.data.mock);
        trackEvent("search_flights", { from: fromCode, to: toCode, date });
      } catch (err) {
        setError("api");
        setFlights([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [fromCode, toCode, date, tripType, travelers]);

  // Fetch return flights when outbound is selected (round trip only)
  const fetchReturnFlights = async () => {
    if (!returnDate) return;
    setReturnLoading(true);
    try {
      const res = await axios.post(`${API}/flights/search`, {
        departure_id: toCode,
        arrival_id: fromCode,
        outbound_date: returnDate,
        return_date: null,
        trip_type: "one_way",
        passengers: parseInt(travelers),
      });
      setReturnFlights(res.data.flights || []);
    } catch {
      setReturnFlights([]);
    } finally {
      setReturnLoading(false);
    }
  };

  // Apply filters and sorting
  const filtered = useMemo(() => {
    let result = [...flights];

    // Stops filter
    if (filters.stops.length > 0) {
      result = result.filter((f) => {
        if (filters.stops.includes("nonstop") && f.stops === 0) return true;
        if (filters.stops.includes("1stop") && f.stops === 1) return true;
        if (filters.stops.includes("2plus") && f.stops >= 2) return true;
        return false;
      });
    }

    // Time filter
    if (filters.times.length > 0) {
      result = result.filter((f) => {
        const hour = parseInt(f.departure_time?.split(":")[0] || "0");
        if (filters.times.includes("morning") && hour >= 6 && hour < 12) return true;
        if (filters.times.includes("afternoon") && hour >= 12 && hour < 18) return true;
        if (filters.times.includes("evening") && (hour >= 18 || hour < 6)) return true;
        return false;
      });
    }

    // Sort
    switch (filters.sort) {
      case "price":
        result.sort((a, b) => (a.original_price || 0) - (b.original_price || 0));
        break;
      case "duration":
        result.sort((a, b) => (a.duration_minutes || 0) - (b.duration_minutes || 0));
        break;
      case "departure":
        result.sort((a, b) => (a.departure_time || "").localeCompare(b.departure_time || ""));
        break;
      default: // best: direct first, then by price
        result.sort((a, b) => {
          if (a.is_best_for_visa && !b.is_best_for_visa) return -1;
          if (!a.is_best_for_visa && b.is_best_for_visa) return 1;
          if (a.is_direct && !b.is_direct) return -1;
          if (!a.is_direct && b.is_direct) return 1;
          return (a.duration_minutes || 0) - (b.duration_minutes || 0);
        });
    }

    return result;
  }, [flights, filters]);

  const handleSelect = (flight) => {
    if (isRoundTrip && !selectedOutbound) {
      // Step 1: outbound selected, now fetch return flights
      setSelectedOutbound(flight);
      fetchReturnFlights();
      return;
    }

    if (isRoundTrip && selectedOutbound) {
      // Step 2: return selected — store both and go to checkout
      localStorage.setItem("selectedFlight", JSON.stringify(selectedOutbound));
      localStorage.setItem("selectedReturnFlight", JSON.stringify(flight));
    } else {
      // One-way: just store outbound
      localStorage.setItem("selectedFlight", JSON.stringify(flight));
      localStorage.removeItem("selectedReturnFlight");
    }
    localStorage.setItem("searchParams", JSON.stringify({
      from: fromCode, to: toCode, date, returnDate, travelers, tripType
    }));
    navigate("/checkout");
  };

  const handleChangeOutbound = () => {
    setSelectedOutbound(null);
    setReturnFlights([]);
  };

  const clearFilters = () => setFilters({ stops: [], times: [], sort: "best" });

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="search-results-page">
      <SEOHead
        title={`Onward Flights from ${fromCode} to ${toCode} | Book from $12 | OnwardFlighTickets`}
        description={`Find verified onward flight tickets from ${fromCode} to ${toCode}. Instant PDF delivery, real airline data. From just $12.`}
        path="/search-results"
      />
      <Header />

      <main className="pt-20">
        {/* Search Summary Bar */}
        <div className="bg-white border-b border-[#e2e8f0] py-4">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="search-summary">
                <span className="font-semibold text-[#1e3a5f]">{fromCode}</span>
                <ArrowRight className="w-4 h-4 text-[#6b7280]" />
                <span className="font-semibold text-[#1e3a5f]">{toCode}</span>
                <span className="text-[#6b7280]">&middot;</span>
                <span className="text-[#1f2937]">{date}</span>
                {returnDate && (
                  <>
                    <span className="text-[#6b7280]">-</span>
                    <span className="text-[#1f2937]">{returnDate}</span>
                  </>
                )}
                <span className="text-[#6b7280]">&middot;</span>
                <span className="text-[#1f2937]">{travelers} {t("search.travelers")}</span>
                <span className="text-xs bg-[#1e3a5f] text-white px-2 py-0.5 rounded-full capitalize">
                  {tripType.replace("-", " ")}
                </span>
              </div>
              <button
                onClick={() => navigate("/")}
                className="text-sm text-[#f97316] font-medium hover:underline flex items-center gap-1"
                data-testid="modify-search-btn"
              >
                {t("results.modify_search")}
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white border-b border-[#e2e8f0] py-5">
          <ProgressBar step={1} />
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
          {/* Mock Notice */}
          {isMock && !loading && flights.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800" data-testid="mock-notice">
              {t("results.mock_notice")}
            </div>
          )}

          {loading ? (
            /* Loading State */
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-6 h-6 border-3 border-[#f97316] border-t-transparent rounded-full animate-spin" />
                <div>
                  <p className="text-sm font-medium text-[#1f2937]">{t("results.searching")}</p>
                  <p className="text-xs text-[#6b7280]">{t("results.searching_desc")}</p>
                </div>
              </div>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => <FlightSkeleton key={i} />)}
              </div>
            </div>
          ) : error === "incomplete" ? (
            /* Incomplete search params */
            <div className="text-center py-16">
              <Plane className="w-16 h-16 text-[#d1d5db] mx-auto mb-4" />
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1e3a5f] mb-2">{t("results.no_results_title")}</h2>
              <p className="text-[#6b7280] mb-6">{t("results.no_results_desc")}</p>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#152e4d] transition-all"
                data-testid="try-again-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("results.try_again")}
              </button>
            </div>
          ) : flights.length === 0 ? (
            /* No results */
            <div className="text-center py-16">
              <Plane className="w-16 h-16 text-[#d1d5db] mx-auto mb-4" />
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1e3a5f] mb-2">{t("results.no_results_title")}</h2>
              <p className="text-[#6b7280] mb-6">{t("results.no_results_desc")}</p>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-[#1e3a5f] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#152e4d] transition-all"
                data-testid="try-again-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("results.try_again")}
              </button>
            </div>
          ) : (
            /* Results Layout */
            <div>
              {/* Round Trip Step Indicator */}
              {isRoundTrip && (
                <div className="flex items-center gap-3 mb-6" data-testid="round-trip-steps">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${selectedOutbound ? "bg-green-50 text-green-700 border border-green-200" : "bg-[#1e3a5f] text-white"}`}>
                    {selectedOutbound ? <Check className="w-4 h-4" /> : <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>}
                    Outbound {fromCode} → {toCode}
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#6b7280]" />
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${selectedOutbound ? "bg-[#1e3a5f] text-white" : "bg-[#f1f5f9] text-[#6b7280]"}`}>
                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                    Return {toCode} → {fromCode}
                  </div>
                </div>
              )}

              {/* Selected Outbound Summary (round trip step 2) */}
              {isRoundTrip && selectedOutbound && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="outbound-summary">
                  <div className="flex items-center gap-4">
                    {selectedOutbound.airline_logo && <img src={selectedOutbound.airline_logo} alt={selectedOutbound.airline} className="w-8 h-8 object-contain" />}
                    <div>
                      <p className="text-sm font-semibold text-green-800">Outbound: {selectedOutbound.airline} {selectedOutbound.flight_number}</p>
                      <p className="text-xs text-green-700">{fromCode} {selectedOutbound.departure_time} → {toCode} {selectedOutbound.arrival_time} &middot; {selectedOutbound.departure_date} &middot; {selectedOutbound.duration_text}</p>
                    </div>
                  </div>
                  <button onClick={handleChangeOutbound} className="text-xs text-green-700 font-medium hover:underline" data-testid="change-outbound-btn">Change</button>
                </div>
              )}

              {/* Section Header */}
              {isRoundTrip && !selectedOutbound && (
                <h3 className="font-['Outfit'] text-base font-semibold text-[#1e3a5f] mb-4">Step 1: Select your outbound flight ({fromCode} → {toCode}, {date})</h3>
              )}
              {isRoundTrip && selectedOutbound && !returnLoading && (
                <h3 className="font-['Outfit'] text-base font-semibold text-[#1e3a5f] mb-4">Step 2: Select your return flight ({toCode} → {fromCode}, {returnDate})</h3>
              )}

              {/* Return Loading */}
              {isRoundTrip && selectedOutbound && returnLoading && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-5 h-5 border-3 border-[#f97316] border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-[#6b7280]">Searching return flights {toCode} → {fromCode}...</p>
                  </div>
                  <div className="space-y-4">{[1, 2, 3].map((i) => <FlightSkeleton key={i} />)}</div>
                </div>
              )}

              {/* Return flights: no results */}
              {isRoundTrip && selectedOutbound && !returnLoading && returnFlights.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-[#6b7280] mb-4">No return flights found for {toCode} → {fromCode} on {returnDate}</p>
                  <button onClick={handleChangeOutbound} className="text-[#f97316] font-medium hover:underline text-sm">Change outbound flight</button>
                </div>
              )}

              <div className="flex gap-8">
                {/* Desktop Filters */}
                <div className="hidden lg:block w-64 shrink-0">
                  <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5 sticky top-24">
                    <FiltersPanel
                      filters={filters}
                      setFilters={setFilters}
                      totalResults={filtered.length}
                      onClear={clearFilters}
                    />
                  </div>
                </div>

                {/* Mobile Filter Button */}
                <div className="lg:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-40">
                  <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                    <SheetTrigger asChild>
                      <button
                        className="bg-[#1e3a5f] text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium"
                        data-testid="mobile-filter-btn"
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                        {t("results.filters")} {filters.stops.length + filters.times.length > 0 && `(${filters.stops.length + filters.times.length})`}
                      </button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="rounded-t-3xl bg-white max-h-[70vh] overflow-auto">
                      <SheetTitle className="sr-only">Filters</SheetTitle>
                      <FiltersPanel
                        filters={filters}
                        setFilters={setFilters}
                        totalResults={filtered.length}
                        onClear={clearFilters}
                      />
                    </SheetContent>
                  </Sheet>
                </div>

                {/* Flight Cards */}
                <div className="flex-1 min-w-0">
                  {/* Show outbound flights OR return flights based on selection step */}
                  {(!isRoundTrip || !selectedOutbound) ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-[#6b7280]">
                          {t("results.results_found", { count: filtered.length })}
                        </p>
                        <div className="lg:hidden">
                          <select
                            value={filters.sort}
                            onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
                            className="border border-[#e2e8f0] rounded-lg px-3 py-1.5 text-xs bg-white text-[#1f2937]"
                            data-testid="mobile-sort-select"
                          >
                            <option value="best">{t("results.sort_best")}</option>
                            <option value="price">{t("results.sort_price")}</option>
                            <option value="duration">{t("results.sort_duration")}</option>
                            <option value="departure">{t("results.sort_departure")}</option>
                          </select>
                        </div>
                      </div>
                      <div className="space-y-4" data-testid="flight-results-list">
                        {filtered.map((flight) => (
                          <FlightCard key={flight.id} flight={flight} onSelect={handleSelect} />
                        ))}
                      </div>
                    </>
                  ) : !returnLoading && returnFlights.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-[#6b7280]">{returnFlights.length} return flights found</p>
                      </div>
                      <div className="space-y-4" data-testid="return-flight-results-list">
                        {returnFlights.map((flight) => (
                          <FlightCard key={flight.id} flight={flight} onSelect={handleSelect} />
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
